import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Button, Card, ChannelPill, Field, Icon, I, MOBILE_SINGLE_COLUMN, Pill, TEXT_WRAP_STYLE, gridColumns, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { listInventoryConnections } from '../inventory/inventoryApi'
import { exportOrders, getOrder, getOrderSummary, getZohoPolicyPreview, listOrders, refreshOrderFromChannel, resetOrderZohoInvoiceSync, resetOrderZohoSync, sendOrderToZoho } from '../orders/ordersApi'
import type { ChannelOrderStatus, ChannelOrderZohoSyncNextAction, ChannelOrderZohoSyncStatus, OrderDetail, OrderInvoiceRow, OrderListItem, OrderPage, OrderStatusHistoryEvent, OrderSummary, OrderZohoSync, ProviderCallProvider, ZohoPolicyPreview, ZohoPolicyWarning } from '../orders/types'
import { hasZohoSyncHistory } from '../orders/zohoVisibility'
import { listChannelConnections } from '../channels/channelsApi'
import type { ChannelConnection, ChannelProvider } from '../channels/types'

const PAGE_SIZE = 10
const EMPTY_VALUE = '—'
const DEFAULT_ORDERS_EXPORT_FILENAME = 'orders.xlsx'
const STATUS_OPTIONS: Array<ChannelOrderStatus | ''> = ['', 'NEW', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'UNKNOWN']
const ZOHO_STATUS_OPTIONS: ChannelOrderZohoSyncStatus[] = ['NOT_CONFIGURED', 'READY', 'SALES_ORDER_CREATED', 'SALES_ORDER_CONFIRMED', 'INVOICE_CREATED', 'SALES_ORDER_VOIDED', 'FAILED']
const SUMMARY_STATUSES: ChannelOrderStatus[] = ['NEW', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'UNKNOWN']

type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral'

const DOT_COLORS: Record<PillTone, string> = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  info: 'var(--info)',
  neutral: 'var(--ink-300)',
  brand: 'var(--brand-500)',
  coral: 'var(--accent-500)',
}

function statusTone(status: ChannelOrderStatus): PillTone {
  if (status === 'NEW') return 'warning'
  if (status === 'IN_PROGRESS') return 'info'
  if (status === 'COMPLETED') return 'success'
  if (status === 'CANCELLED') return 'danger'
  return 'neutral'
}

function providerDisplayName(provider: ChannelProvider | ProviderCallProvider): string {
  switch (provider) {
    case 'ZID': return 'Zid'
    case 'SALLA': return 'Salla'
    case 'AMAZON': return 'Amazon'
    case 'TRENDYOL': return 'Trendyol'
    case 'NOON': return 'Noon'
    case 'ZOHO': return 'Zoho'
    default: return provider
  }
}

function orderLabel(order: Pick<OrderListItem, 'externalOrderNumber' | 'externalOrderId'>): string {
  return order.externalOrderNumber?.trim() || order.externalOrderId
}

function displayDate(order: Pick<OrderListItem, 'placedAt' | 'externalUpdatedAt' | 'lastSeenAt'>): string | null {
  return order.placedAt ?? order.externalUpdatedAt ?? order.lastSeenAt
}

function paymentPrimary(order: Pick<OrderListItem, 'paymentMethodName' | 'paymentMethodCode' | 'paymentMethodType' | 'paymentStatus'>): string {
  return order.paymentMethodName?.trim() || order.paymentMethodCode?.trim() || order.paymentMethodType?.trim() || order.paymentStatus?.trim() || EMPTY_VALUE
}

function paymentMethodDisplay(order: Pick<OrderListItem, 'paymentMethodName' | 'paymentMethodCode'>): string {
  return order.paymentMethodName?.trim() || order.paymentMethodCode?.trim() || EMPTY_VALUE
}

function paymentSecondary(order: Pick<OrderListItem, 'paymentTransactionStatus'>): string | null {
  return order.paymentTransactionStatus?.trim() || null
}

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return EMPTY_VALUE
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function formatNumber(value: number | null | undefined, locale: string): string {
  if (value == null) return EMPTY_VALUE
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
}

function formatMoney(value: number | null | undefined, currency: string | null | undefined, locale: string, fallbackCurrency: string): string {
  if (value == null) return EMPTY_VALUE
  return `${formatNumber(value, locale)} ${currency?.trim() || fallbackCurrency}`
}

function invoiceAmountDisplay(row: OrderInvoiceRow, currency: string | null | undefined, locale: string, fallbackCurrency: string): string {
  const formattedAmount = row.formattedAmount?.trim()
  if (formattedAmount) return formattedAmount
  return formatMoney(row.amount, currency, locale, fallbackCurrency)
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.orders.errors.codes.${error.code}`
  }
  return 'pages.orders.errors.codes.REQUEST_FAILED'
}

function exportErrorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.orders.export.errors.codes.${error.code}`
  }
  return 'pages.orders.export.errors.codes.REQUEST_FAILED'
}

function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = window.URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    window.URL.revokeObjectURL(objectUrl)
  }
}

interface OrdersPageProps {
  accessToken: string
  onOpenOrder?: (orderId: number) => void
  onSummaryLoaded?: (summary: OrderSummary) => void
  onOpenOrderRouting?: (inventoryConnectionId: number, orderId: number) => void
}

interface DetailFieldProps {
  label: string
  value: string
}

function DetailField({ label, value }: DetailFieldProps) {
  return (
    <div style={{ padding: 12, borderRadius: 12, background: 'var(--ink-50)', minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 4 }}>{label}</div>
      <div style={{ ...TEXT_WRAP_STYLE, fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>{value}</div>
    </div>
  )
}

function DetailTextBlock({ label, value }: DetailFieldProps) {
  return (
    <div style={{ padding: 12, borderRadius: 12, background: 'var(--ink-50)', minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 4 }}>{label}</div>
      <div style={{ ...TEXT_WRAP_STYLE, fontSize: 13, fontWeight: 700, color: 'var(--fg)', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{value}</div>
    </div>
  )
}

function MobileDetailValue({ label, value, mono = false, strong = false }: DetailFieldProps & { mono?: boolean, strong?: boolean }) {
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{label}</span>
      <span style={{ ...TEXT_WRAP_STYLE, fontFamily: mono ? 'var(--font-mono)' : undefined, fontSize: mono ? 11.5 : 13, fontWeight: strong ? 800 : 700, color: strong ? 'var(--fg)' : 'var(--fg-muted)' }}>{value}</span>
    </div>
  )
}

function previewValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE
  return String(value)
}

function previewSourceLabel(source: ZohoPolicyPreview['policySource'], t: ReturnType<typeof useTranslation>['t']): string {
  return String(t(`pages.orders.zohoPreview.sources.${source}`, { defaultValue: source }))
}

function previewIssueLabel(issue: ZohoPolicyWarning, t: ReturnType<typeof useTranslation>['t']): string {
  return String(t(`pages.orders.zohoPreview.codes.${issue.code}`, { defaultValue: issue.code }))
}

function zohoReasonLabel(reasonCode: string | null | undefined, t: ReturnType<typeof useTranslation>['t']): string {
  if (!reasonCode) return String(t('pages.orders.zoho.reasons.NONE'))
  return String(t(`pages.orders.zoho.reasons.${reasonCode}`, { defaultValue: reasonCode }))
}

function zohoNextActionLabel(nextAction: ChannelOrderZohoSyncNextAction | null | undefined, t: ReturnType<typeof useTranslation>['t']): string {
  return String(t(`pages.orders.zoho.nextActions.${nextAction ?? 'NONE'}`, { defaultValue: nextAction ?? 'NONE' }))
}

function mergeOrderZohoSync<T extends OrderListItem>(order: T, orderId: number, zohoSync: OrderZohoSync): T {
  if (order.id !== orderId) return order
  return { ...order, zohoSync }
}

function PreviewIssueList({
  title,
  emptyLabel,
  items,
  tone,
  t,
}: {
  title: string
  emptyLabel: string
  items: ZohoPolicyWarning[]
  tone: 'warning' | 'danger'
  t: ReturnType<typeof useTranslation>['t']
}) {
  const color = tone === 'danger' ? 'var(--danger)' : 'var(--warning)'
  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, background: 'white', overflow: 'hidden' }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)', fontSize: 12, fontWeight: 800, color }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ padding: 12, fontSize: 12.5, color: 'var(--fg-muted)' }}>{emptyLabel}</div>
      ) : (
        <div style={{ display: 'grid' }}>
          {items.map((issue, index) => (
            <div key={`${issue.code}-${issue.field ?? ''}-${issue.externalId ?? ''}-${index}`} style={{ padding: 12, borderTop: index === 0 ? undefined : '1px solid var(--border-subtle)', borderInlineStart: `3px solid ${color}` }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--fg)' }}>{previewIssueLabel(issue, t)}</div>
              <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
                {issue.field && <span>{t('pages.orders.zohoPreview.field')}: {issue.field}</span>}
                {issue.externalId && <span>{t('pages.orders.zohoPreview.externalId')}: {issue.externalId}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function PayloadBlock({ title, payload }: { title: string, payload: Record<string, unknown> }) {
  return (
    <div style={{ minWidth: 0, maxWidth: '100%' }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--brand-900)' }}>{title}</h4>
      <pre dir="ltr" style={{ margin: 0, padding: 12, borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'var(--ink-900)', color: 'white', overflowX: 'auto', maxWidth: '100%', fontFamily: 'var(--font-mono)', fontSize: 11.5, lineHeight: 1.55, textAlign: 'start' }}>
        {JSON.stringify(payload ?? {}, null, 2)}
      </pre>
    </div>
  )
}

function ZohoPreviewPanel({ preview, t, isMobile }: { preview: ZohoPolicyPreview, t: ReturnType<typeof useTranslation>['t'], isMobile: boolean }) {
  return (
    <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(150px, 1fr))'), gap: 10 }}>
        <DetailField label={t('pages.orders.zohoPreview.policyId')} value={previewValue(preview.policyId)} />
        <DetailField label={t('pages.orders.zohoPreview.policySource')} value={previewSourceLabel(preview.policySource, t)} />
        <DetailField label={t('pages.orders.zohoPreview.policyVersion')} value={previewValue(preview.policyVersion)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(150px, 1fr))'), gap: 10 }}>
        <DetailField label={t('pages.orders.zohoPreview.action')} value={preview.actionPlan?.action ? zohoNextActionLabel(preview.actionPlan.action, t) : previewValue(null)} />
        <DetailField label={t('pages.orders.zohoPreview.actionMappingId')} value={previewValue(preview.matchedActionMappingId ?? preview.actionPlan?.matchedActionMappingId)} />
        <DetailField label={t('pages.orders.zohoPreview.warehouseOverrideRuleId')} value={previewValue(preview.matchedWarehouseOverrideRuleId)} />
        <DetailField label={t('pages.orders.zohoPreview.paymentMappingId')} value={previewValue(preview.matchedPaymentMappingId)} />
        <DetailField label={t('pages.orders.zohoPreview.shippingMappingId')} value={previewValue(preview.matchedShippingMappingId)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(240px, 1fr))'), gap: 10 }}>
        <PreviewIssueList title={t('pages.orders.zohoPreview.warnings')} emptyLabel={t('pages.orders.zohoPreview.noWarnings')} items={preview.warnings} tone="warning" t={t} />
        <PreviewIssueList title={t('pages.orders.zohoPreview.blockingErrors')} emptyLabel={t('pages.orders.zohoPreview.noBlockingErrors')} items={preview.blockingErrors} tone="danger" t={t} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(280px, 1fr))'), gap: 12 }}>
        <PayloadBlock title={t('pages.orders.zohoPreview.salesOrderPayload')} payload={preview.salesOrderPayload} />
        <PayloadBlock title={t('pages.orders.zohoPreview.invoicePayload')} payload={preview.invoicePayload} />
      </div>
    </div>
  )
}

function statusHistoryText(event: OrderStatusHistoryEvent, t: ReturnType<typeof useTranslation>['t']): string {
  if (event.eventType === 'STATUS_CHANGED') {
    return String(t('pages.orders.history.statusReported', {
      provider: providerDisplayName(event.provider),
      status: t(`pages.orders.status.${event.status}`),
    }))
  }
  if (event.eventType === 'PAYMENT_STATUS_CHANGED' && event.paymentStatus) {
    return String(t('pages.orders.history.paymentReported', { provider: providerDisplayName(event.provider), status: event.paymentStatus }))
  }
  if (event.eventType === 'ORDER_UPDATED') return String(t('pages.orders.history.orderUpdated', { provider: providerDisplayName(event.provider) }))
  return String(t('pages.orders.history.orderReceived', { provider: providerDisplayName(event.provider) }))
}

function StatusHistoryRow({ event, locale, t }: { event: OrderStatusHistoryEvent, locale: string, t: ReturnType<typeof useTranslation>['t'] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10, alignItems: 'start' }}>
      <span style={{ width: 9, height: 9, marginTop: 5, borderRadius: '50%', background: DOT_COLORS[statusTone(event.status)] }} />
      <div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--fg)' }}>{statusHistoryText(event, t)}</span>
          <Pill tone={statusTone(event.status)} dot size="sm">{t(`pages.orders.status.${event.status}`)}</Pill>
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--fg-subtle)' }}>
          {formatDate(event.occurredAt ?? event.createdAt, locale)} · {providerDisplayName(event.provider)}
          {event.rawStatus ? ` · ${event.rawStatus}` : ''}
        </div>
      </div>
    </div>
  )
}

export function OrdersPage({ accessToken, onOpenOrder, onSummaryLoaded, onOpenOrderRouting }: OrdersPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const initialSearch = useMemo(() => new URLSearchParams(onOpenOrder ? window.location.search : ''), [onOpenOrder])
  const initialQuery = initialSearch.get('query') ?? ''
  const [query, setQuery] = useState(initialQuery)
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery)
  const [dateFrom, setDateFrom] = useState(initialSearch.get('dateFrom') ?? '')
  const [dateTo, setDateTo] = useState(initialSearch.get('dateTo') ?? '')
  const [status, setStatus] = useState<ChannelOrderStatus | ''>((initialSearch.get('status') as ChannelOrderStatus) ?? '')
  const [channelConnectionId, setChannelConnectionId] = useState(initialSearch.get('channelConnectionId') ?? '')
  const [channels, setChannels] = useState<ChannelConnection[]>([])
  const [channelsLoading, setChannelsLoading] = useState(true)
  const [channelsError, setChannelsError] = useState<unknown>(null)
  const [zohoFilterAvailable, setZohoFilterAvailable] = useState(false)
  const [zohoStatus, setZohoStatus] = useState<ChannelOrderZohoSyncStatus | ''>((initialSearch.get('zohoStatus') as ChannelOrderZohoSyncStatus) ?? '')
  const [page, setPage] = useState(() => Math.max(0, Number(initialSearch.get('page') ?? 0) || 0))
  const [data, setData] = useState<OrderPage<OrderListItem> | null>(null)
  const [summary, setSummary] = useState<OrderSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [summaryError, setSummaryError] = useState<unknown>(null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<unknown>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const [detail, setDetail] = useState<OrderDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<unknown>(null)
  const [channelRefreshLoading, setChannelRefreshLoading] = useState(false)
  const [channelRefreshError, setChannelRefreshError] = useState<unknown>(null)
  const [zohoPreview, setZohoPreview] = useState<ZohoPolicyPreview | null>(null)
  const [zohoPreviewLoading, setZohoPreviewLoading] = useState(false)
  const [zohoPreviewError, setZohoPreviewError] = useState<unknown>(null)
  const [zohoSendLoading, setZohoSendLoading] = useState(false)
  const [zohoResetLoading, setZohoResetLoading] = useState(false)
  const [zohoSendError, setZohoSendError] = useState<unknown>(null)
  const selectedOrderIdRef = useRef<number | null>(null)
  const zohoPreviewRequestIdRef = useRef(0)
  const zohoStatusRef = useRef<ChannelOrderZohoSyncStatus | ''>('')
  const activeZohoStatus = zohoFilterAvailable ? zohoStatus : ''

  useEffect(() => {
    if (!onOpenOrder) return
    const search = new URLSearchParams()
    if (debouncedQuery) search.set('query', debouncedQuery)
    if (status) search.set('status', status)
    if (channelConnectionId) search.set('channelConnectionId', channelConnectionId)
    if (activeZohoStatus) search.set('zohoStatus', activeZohoStatus)
    if (dateFrom) search.set('dateFrom', dateFrom)
    if (dateTo) search.set('dateTo', dateTo)
    if (page) search.set('page', String(page))
    const next = `/orders${search.size ? `?${search}` : ''}`
    if (`${window.location.pathname}${window.location.search}` !== next) window.history.replaceState({}, '', next)
  }, [activeZohoStatus, channelConnectionId, dateFrom, dateTo, debouncedQuery, onOpenOrder, page, status])

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQuery(query)
      setPage(0)
    }, 300)
    return () => window.clearTimeout(id)
  }, [query])

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChannelsLoading(true)
    setChannelsError(null)
    listChannelConnections(accessToken)
      .then(connections => {
        if (!cancelled) setChannels(connections)
      })
      .catch(caught => {
        if (!cancelled) setChannelsError(caught)
      })
      .finally(() => {
        if (!cancelled) setChannelsLoading(false)
      })
    return () => { cancelled = true }
  }, [accessToken, refreshNonce])

  useEffect(() => {
    let cancelled = false
    listInventoryConnections(accessToken)
      .then(connections => {
        if (!cancelled) {
          const hasConnectedZoho = connections.some(connection => connection.provider === 'ZOHO' && connection.status === 'CONNECTED')
          setZohoFilterAvailable(hasConnectedZoho)
          if (!hasConnectedZoho && zohoStatusRef.current) {
            zohoStatusRef.current = ''
            setZohoStatus('')
            setPage(0)
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setZohoFilterAvailable(false)
          if (zohoStatusRef.current) {
            zohoStatusRef.current = ''
            setZohoStatus('')
            setPage(0)
          }
        }
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, refreshNonce])


  useEffect(() => {
    let cancelled = false
    // Intentional reset on dependency change before starting the async list request.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)

    listOrders(accessToken, { page, size: PAGE_SIZE, query: debouncedQuery, status, channelConnectionId: channelConnectionId ? Number(channelConnectionId) : undefined, zohoStatus: activeZohoStatus, dateFrom, dateTo })
      .then(response => {
        if (!cancelled) setData(response)
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, activeZohoStatus, channelConnectionId, dateFrom, dateTo, debouncedQuery, page, refreshNonce, status])

  useEffect(() => {
    let cancelled = false
    // Intentional reset on refresh before starting the async summary request.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSummaryLoading(true)
    setSummaryError(null)

    getOrderSummary(accessToken)
      .then(response => {
        if (!cancelled) {
          setSummary(response)
          onSummaryLoaded?.(response)
        }
      })
      .catch(caught => {
        if (!cancelled) setSummaryError(caught)
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, onSummaryLoaded, refreshNonce])

  useEffect(() => {
    if (selectedOrderId == null) return

    let cancelled = false
    // Intentional reset when a new order detail request starts.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDetailLoading(true)
    setDetailError(null)

    getOrder(accessToken, selectedOrderId)
      .then(response => {
        if (!cancelled) setDetail(response)
      })
      .catch(caught => {
        if (!cancelled) setDetailError(caught)
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, selectedOrderId])

  useEffect(() => {
    selectedOrderIdRef.current = selectedOrderId
  }, [selectedOrderId])

  const rows = useMemo<OrderListItem[]>(() => data?.items ?? [], [data?.items])
  const total = data?.totalElements ?? 0
  const fallbackCurrency = String(t('pages.orders.currencyFallback'))
  const headers = [
    t('pages.orders.table.order'),
    t('pages.orders.table.channel'),
    t('pages.orders.table.carrier'),
    t('pages.orders.table.date'),
    t('pages.orders.table.status'),
    t('pages.orders.table.payment'),
    t('pages.orders.table.customer'),
    t('pages.orders.table.zoho'),
    t('pages.orders.table.total'),
    '',
  ]

  const refresh = () => setRefreshNonce(current => current + 1)
  const handleExport = async () => {
    if (exporting) return
    setExportError(null)
    setExporting(true)
    try {
      const download = await exportOrders(accessToken, {
        query: debouncedQuery,
        status,
        channelConnectionId: channelConnectionId ? Number(channelConnectionId) : undefined,
        zohoStatus: activeZohoStatus,
        dateFrom,
        dateTo,
      })
      downloadBlob(download.blob, download.filename ?? DEFAULT_ORDERS_EXPORT_FILENAME)
    } catch (caught) {
      setExportError(caught)
    } finally {
      setExporting(false)
    }
  }
  const resetZohoPreview = () => {
    zohoPreviewRequestIdRef.current += 1
    setZohoPreview(null)
    setZohoPreviewError(null)
    setZohoPreviewLoading(false)
  }
  const resetZohoSend = () => {
    setZohoSendLoading(false)
    setZohoResetLoading(false)
    setZohoSendError(null)
  }
  const resetChannelRefresh = () => {
    setChannelRefreshLoading(false)
    setChannelRefreshError(null)
  }
  const openDetail = (id: number) => {
    if (onOpenOrder) {
      onOpenOrder(id)
      return
    }
    setDetail(null)
    setDetailError(null)
    resetZohoPreview()
    resetZohoSend()
    resetChannelRefresh()
    setSelectedOrderId(id)
  }
  const closeDetail = () => {
    setSelectedOrderId(null)
    setDetail(null)
    setDetailError(null)
    setDetailLoading(false)
    resetZohoPreview()
    resetZohoSend()
    resetChannelRefresh()
  }
  const requestZohoPreview = (orderId: number) => {
    const requestId = zohoPreviewRequestIdRef.current + 1
    zohoPreviewRequestIdRef.current = requestId
    setZohoPreview(null)
    setZohoPreviewLoading(true)
    setZohoPreviewError(null)
    getZohoPolicyPreview(accessToken, orderId)
      .then(response => {
        if (zohoPreviewRequestIdRef.current === requestId && selectedOrderIdRef.current === orderId) {
          setZohoPreview(response)
        }
      })
      .catch(caught => {
        if (zohoPreviewRequestIdRef.current === requestId && selectedOrderIdRef.current === orderId) {
          setZohoPreviewError(caught)
        }
      })
      .finally(() => {
        if (zohoPreviewRequestIdRef.current === requestId && selectedOrderIdRef.current === orderId) {
          setZohoPreviewLoading(false)
        }
      })
  }
  const loadZohoPreview = () => {
    if (!detail) return
    requestZohoPreview(detail.id)
  }
  const refreshSelectedOrderFromChannel = () => {
    if (!detail || channelRefreshLoading || detailLoading || zohoSendLoading || zohoResetLoading) return
    const orderId = detail.id
    const shouldReloadPreview = zohoPreview !== null
    setChannelRefreshLoading(true)
    setChannelRefreshError(null)
    refreshOrderFromChannel(accessToken, orderId)
      .then(response => {
        if (selectedOrderIdRef.current !== orderId) return
        setDetail(response)
        setData(current => current ? { ...current, items: current.items.map(order => order.id === orderId ? response : order) } : current)
        resetZohoPreview()
        setRefreshNonce(current => current + 1)
        if (shouldReloadPreview) {
          requestZohoPreview(orderId)
        }
      })
      .catch(caught => {
        if (selectedOrderIdRef.current === orderId) {
          setChannelRefreshError(caught)
        }
      })
      .finally(() => {
        if (selectedOrderIdRef.current === orderId) {
          setChannelRefreshLoading(false)
        }
      })
  }
  const sendSelectedOrderToZoho = () => {
    if (!detail || !detail.zohoSync.canSend || zohoSendLoading || zohoResetLoading) return
    const orderId = detail.id
    const shouldReloadPreview = zohoPreview !== null
    setZohoSendLoading(true)
    setZohoSendError(null)
    sendOrderToZoho(accessToken, orderId)
      .then(zohoSync => {
        if (selectedOrderIdRef.current !== orderId) return
        setDetail(current => current ? mergeOrderZohoSync(current, orderId, zohoSync) : current)
        setData(current => current ? { ...current, items: current.items.map(order => mergeOrderZohoSync(order, orderId, zohoSync)) } : current)
        resetZohoPreview()
        if (shouldReloadPreview) {
          requestZohoPreview(orderId)
        }
      })
      .catch(caught => {
        if (selectedOrderIdRef.current === orderId) {
          setZohoSendError(caught)
        }
      })
      .finally(() => {
        if (selectedOrderIdRef.current === orderId) {
          setZohoSendLoading(false)
        }
      })
  }
  const canResetZohoInvoice = Boolean(detail?.zohoSync.salesOrderId && detail?.zohoSync.invoiceId)
  const canResetZohoSync = Boolean(detail?.zohoSync.salesOrderId || detail?.zohoSync.invoiceId || detail?.zohoSync.creditNoteId)
  const resetSelectedOrderZohoInvoice = () => {
    if (!detail || !canResetZohoInvoice || zohoSendLoading || zohoResetLoading) return
    if (!window.confirm(String(t('pages.orders.actions.resetZohoInvoiceConfirm')))) return
    const orderId = detail.id
    const shouldReloadPreview = zohoPreview !== null
    setZohoResetLoading(true)
    setZohoSendError(null)
    resetOrderZohoInvoiceSync(accessToken, orderId)
      .then(zohoSync => {
        if (selectedOrderIdRef.current !== orderId) return
        setDetail(current => current ? mergeOrderZohoSync(current, orderId, zohoSync) : current)
        setData(current => current ? { ...current, items: current.items.map(order => mergeOrderZohoSync(order, orderId, zohoSync)) } : current)
        resetZohoPreview()
        if (shouldReloadPreview) {
          requestZohoPreview(orderId)
        }
      })
      .catch(caught => {
        if (selectedOrderIdRef.current === orderId) {
          setZohoSendError(caught)
        }
      })
      .finally(() => {
        if (selectedOrderIdRef.current === orderId) {
          setZohoResetLoading(false)
        }
      })
  }
  const resetSelectedOrderZohoSync = () => {
    if (!detail || !canResetZohoSync || zohoSendLoading || zohoResetLoading) return
    if (!window.confirm(String(t('pages.orders.actions.resetZohoSyncConfirm')))) return
    const orderId = detail.id
    const shouldReloadPreview = zohoPreview !== null
    setZohoResetLoading(true)
    setZohoSendError(null)
    resetOrderZohoSync(accessToken, orderId)
      .then(zohoSync => {
        if (selectedOrderIdRef.current !== orderId) return
        setDetail(current => current ? mergeOrderZohoSync(current, orderId, zohoSync) : current)
        setData(current => current ? { ...current, items: current.items.map(order => mergeOrderZohoSync(order, orderId, zohoSync)) } : current)
        resetZohoPreview()
        if (shouldReloadPreview) {
          requestZohoPreview(orderId)
        }
      })
      .catch(caught => {
        if (selectedOrderIdRef.current === orderId) {
          setZohoSendError(caught)
        }
      })
      .finally(() => {
        if (selectedOrderIdRef.current === orderId) {
          setZohoResetLoading(false)
        }
      })
  }
  const isRefreshing = loading || summaryLoading
  const selectedOrderLabel = detail ? orderLabel(detail) : selectedOrderId == null ? '' : String(selectedOrderId)
  const hasActiveFilter = Boolean(debouncedQuery.trim() || status || channelConnectionId || activeZohoStatus)
  const showZohoColumn = zohoFilterAvailable || rows.some(order => hasZohoSyncHistory(order.zohoSync))
  const zohoDetailConnected = detail?.zohoSync.reasonCode !== 'ZOHO_INTEGRATION_NOT_AVAILABLE'
  const showZohoDetail = detail != null && (zohoDetailConnected || hasZohoSyncHistory(detail.zohoSync))
  const visibleHeaders = showZohoColumn ? headers : headers.filter(header => header !== t('pages.orders.table.zoho'))

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
        {SUMMARY_STATUSES.map(summaryStatus => {
          const tone = statusTone(summaryStatus)
          const selected = status === summaryStatus
          return (
            <Card
              key={summaryStatus}
              padding={14}
              onClick={() => {
                setStatus(current => current === summaryStatus ? '' : summaryStatus)
                setPage(0)
              }}
              style={{ cursor: 'pointer', borderColor: selected ? 'var(--brand-500)' : undefined, background: selected ? 'var(--brand-100)' : undefined }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>
                {t(`pages.orders.status.${summaryStatus}`)}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--brand-900)', lineHeight: 1, letterSpacing: '-0.02em' }}>
                  {summaryLoading ? '…' : (summary?.byStatus?.[summaryStatus] ?? 0).toLocaleString(i18n.language)}
                </div>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: DOT_COLORS[tone] }} />
              </div>
            </Card>
          )
        })}
      </div>

      {summaryError !== null && (
        <Card padding={16} style={{ marginBottom: 14, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(errorMessageKey(summaryError), { defaultValue: String(t('pages.orders.errors.codes.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      <Card padding={12} style={{ marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: isMobile ? 'stretch' : 'center' }}>
        <Field
          label={t('pages.orders.filters.dateFrom')}
          type="date"
          value={dateFrom}
          onChange={event => {
            const value = event.target.value
            setDateFrom(value)
            if (value && dateTo && value > dateTo) setDateTo(value)
            setPage(0)
          }}
          style={{ minWidth: 0, width: isMobile ? '100%' : undefined }}
        />
        <Field
          label={t('pages.orders.filters.dateTo')}
          type="date"
          value={dateTo}
          onChange={event => {
            const value = event.target.value
            setDateTo(value)
            if (value && dateFrom && value < dateFrom) setDateFrom(value)
            setPage(0)
          }}
          style={{ minWidth: 0, width: isMobile ? '100%' : undefined }}
        />
        <label style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '1 1 260px' }}>
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>{t('pages.orders.searchLabel')}</span>
          <div style={{ position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-subtle)', display: 'flex' }}>
            <Icon size={15}>{I.search}</Icon>
          </div>
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t('pages.orders.searchPlaceholder')}
            style={{
              paddingBlock: '9px', paddingInlineStart: 36, paddingInlineEnd: 14, fontSize: 13,
              border: '1px solid var(--border-subtle)', borderRadius: 9, width: '100%',
              fontFamily: 'inherit', background: 'var(--ink-50)', outline: 'none', color: 'var(--fg)',
            }}
          />
        </label>
        <label style={{ display: isMobile ? 'grid' : 'inline-flex', gridTemplateColumns: isMobile ? '1fr' : undefined, alignItems: isMobile ? 'stretch' : 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--fg-muted)', width: isMobile ? '100%' : undefined }}>
          {t('pages.orders.filters.status')}
          <select
            value={status}
            onChange={event => {
              setStatus(event.target.value as ChannelOrderStatus | '')
              setPage(0)
            }}
            style={{
              padding: '9px 12px', fontSize: 13, fontWeight: 600, color: 'var(--fg)',
              background: 'white', border: '1px solid var(--border-subtle)', borderRadius: 9,
              cursor: 'pointer', fontFamily: 'inherit', minWidth: isMobile ? 0 : 160, width: isMobile ? '100%' : undefined,
            }}
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option || 'all'} value={option}>{t(option ? `pages.orders.status.${option}` : 'pages.orders.filters.allStatuses')}</option>
            ))}
          </select>
        </label>
        <label style={{ display: isMobile ? 'grid' : 'inline-flex', gridTemplateColumns: isMobile ? '1fr' : undefined, alignItems: isMobile ? 'stretch' : 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--fg-muted)', width: isMobile ? '100%' : undefined }}>
          {t('pages.orders.filters.channel')}
          <select
            value={channelConnectionId}
            disabled={channelsLoading || channelsError !== null}
            onChange={event => {
              setChannelConnectionId(event.target.value)
              setPage(0)
            }}
            style={{
              padding: '9px 12px', fontSize: 13, fontWeight: 600, color: 'var(--fg)',
              background: 'white', border: '1px solid var(--border-subtle)', borderRadius: 9,
              cursor: 'pointer', fontFamily: 'inherit', minWidth: isMobile ? 0 : 150, width: isMobile ? '100%' : undefined,
            }}
          >
            <option value="">{t('pages.orders.filters.allChannels')}</option>
            {channels.map(channel => (
              <option key={channel.id} value={channel.id}>{channel.displayName} · {t(`channels.providers.${channel.provider}`)}</option>
            ))}
          </select>
        </label>
        {channelsError !== null && <span role="alert">{String(t(errorMessageKey(channelsError)))}</span>}
        {zohoFilterAvailable && (
          <label style={{ display: isMobile ? 'grid' : 'inline-flex', gridTemplateColumns: isMobile ? '1fr' : undefined, alignItems: isMobile ? 'stretch' : 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--fg-muted)', width: isMobile ? '100%' : undefined }}>
            {t('pages.orders.filters.zoho')}
            <select
              value={zohoStatus}
              onChange={event => {
                const nextZohoStatus = event.target.value as ChannelOrderZohoSyncStatus | ''
                zohoStatusRef.current = nextZohoStatus
                setZohoStatus(nextZohoStatus)
                setPage(0)
              }}
              style={{
                padding: '9px 12px', fontSize: 13, fontWeight: 600, color: 'var(--fg)',
                background: 'white', border: '1px solid var(--border-subtle)', borderRadius: 9,
                cursor: 'pointer', fontFamily: 'inherit', minWidth: isMobile ? 0 : 170, width: isMobile ? '100%' : undefined,
              }}
            >
              <option value="">{t('pages.orders.filters.zohoAny')}</option>
              {ZOHO_STATUS_OPTIONS.map(option => (
                <option key={option} value={option}>{t(`pages.orders.zoho.${option}`)}</option>
              ))}
            </select>
          </label>
        )}
        <div style={{ marginInlineStart: isMobile ? 0 : 'auto', display: 'flex', gap: 8, alignItems: isMobile ? 'stretch' : 'center', justifyContent: isMobile ? 'space-between' : undefined, flexWrap: 'wrap', width: isMobile ? '100%' : undefined }}>
          <span style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
            {summaryLoading ? t('pages.orders.summaryLoading') : t('pages.orders.summaryTotal', { count: summary?.total ?? total })}
          </span>
          <Button kind="secondary" size="sm" icon={I.download} onClick={handleExport} disabled={exporting} style={mobileButtonStyle(isMobile)}>
            {exporting ? t('pages.orders.actions.exporting') : t('pages.orders.actions.export')}
          </Button>
          <Button kind="primary" size="sm" icon={I.refresh} onClick={refresh} disabled={isRefreshing} style={mobileButtonStyle(isMobile)}>
            {isRefreshing ? t('pages.orders.actions.refreshing') : t('pages.orders.actions.refresh')}
          </Button>
        </div>
      </Card>

      {exportError !== null && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(exportErrorMessageKey(exportError), { defaultValue: String(t('pages.orders.export.errors.codes.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      {loading && (
        <Card padding={22} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.refresh}</Icon>
            {t('pages.orders.loading')}
          </div>
        </Card>
      )}

      {error !== null && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(errorMessageKey(error), { defaultValue: String(t('pages.orders.errors.codes.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      <Card padding={0} style={{ overflow: 'hidden' }}>
        {isMobile ? (
          <div style={{ display: 'grid' }}>
            {!loading && error === null && rows.length === 0 && (
              <div style={{ padding: '36px 22px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                <div style={{ fontWeight: 800, color: 'var(--fg)', marginBottom: 5 }}>{t('pages.orders.empty.title')}</div>
                <div>{hasActiveFilter ? t('pages.orders.emptyFiltered') : t('pages.orders.empty.body')}</div>
              </div>
            )}
            {rows.map(order => (
              <div
                key={order.id}
                style={{ borderTop: '1px solid var(--border-subtle)', background: selectedOrderId === order.id ? 'var(--brand-100)' : undefined }}
              >
                <div style={{ display: 'grid', gap: 12, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--brand-700)', fontWeight: 800, overflowWrap: 'anywhere' }}>{orderLabel(order)}</div>
                      <div style={{ marginTop: 7, display: 'grid', gap: 4 }}>
                        <ChannelPill name={providerDisplayName(order.provider)} size="sm" />
                        {order.channelDisplayName && <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{order.channelDisplayName}</span>}
                      </div>
                    </div>
                    <Pill tone={statusTone(order.status)} dot size="sm">{t(`pages.orders.status.${order.status}`)}</Pill>
                  </div>
                  <div style={{ display: 'grid', gap: 8, fontSize: 12.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('pages.orders.table.date')}</span>
                      <span style={{ color: 'var(--fg)', textAlign: 'end' }}>{formatDate(displayDate(order), i18n.language)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('pages.orders.table.payment')}</span>
                      <span style={{ color: 'var(--fg)', textAlign: 'end' }}>{paymentPrimary(order)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('pages.orders.table.total')}</span>
                      <strong style={{ color: 'var(--fg)', textAlign: 'end' }}>{formatMoney(order.totalAmount, order.currency, i18n.language, fallbackCurrency)}</strong>
                    </div>
                    {showZohoColumn && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('pages.orders.table.zoho')}</span>
                        <Pill tone="neutral" dot size="sm">{t(`pages.orders.zoho.${order.zohoSync.status}`)}</Pill>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    aria-label={String(t('pages.orders.actions.openDetail', { order: orderLabel(order) }))}
                    onClick={() => openDetail(order.id)}
                    style={{
                      justifySelf: 'end',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '7px 10px',
                      borderRadius: 8,
                      border: '1px solid var(--border-subtle)',
                      background: 'white',
                      color: 'var(--fg-muted)',
                      font: 'inherit',
                      fontSize: 12,
                      fontWeight: 800,
                      textAlign: 'start',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon size={15}>{I.eye}</Icon>
                    {t('pages.orders.detail.title')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ink-50)' }}>
                {visibleHeaders.map((header, index) => (
                  <th key={index} style={{ textAlign: index === visibleHeaders.length - 1 ? 'end' : 'start', padding: '11px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && error === null && rows.length === 0 && (
                <tr>
                  <td colSpan={visibleHeaders.length} style={{ padding: '36px 22px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                    <div style={{ fontWeight: 800, color: 'var(--fg)', marginBottom: 5 }}>{t('pages.orders.empty.title')}</div>
                    <div>{hasActiveFilter ? t('pages.orders.emptyFiltered') : t('pages.orders.empty.body')}</div>
                  </td>
                </tr>
              )}
              {rows.map(order => (
                <tr
                  key={order.id}
                  onClick={() => openDetail(order.id)}
                  style={{ borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', background: selectedOrderId === order.id ? 'var(--brand-100)' : undefined }}
                >
                  <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--brand-700)', fontWeight: 600 }}>{orderLabel(order)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <ChannelPill name={providerDisplayName(order.provider)} size="sm" />
                      {order.channelDisplayName && <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{order.channelDisplayName}</span>}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--fg-muted)', fontSize: 12.5 }}>{order.shippingCompany?.trim() || EMPTY_VALUE}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--fg-muted)' }}>{formatDate(displayDate(order), i18n.language)}</td>
                  <td style={{ padding: '12px 16px' }}><Pill tone={statusTone(order.status)} dot size="sm">{t(`pages.orders.status.${order.status}`)}</Pill></td>
                  <td style={{ padding: '12px 16px', color: 'var(--fg-muted)', fontSize: 12.5 }}>
                    <div style={{ display: 'grid', gap: 3 }}>
                      <span style={{ fontWeight: 700, color: 'var(--fg)' }}>{paymentPrimary(order)}</span>
                      {paymentSecondary(order) && <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{paymentSecondary(order)}</span>}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--fg-muted)', fontSize: 12.5 }}>{order.customerName ?? EMPTY_VALUE}</td>
                  {showZohoColumn && <td style={{ padding: '12px 16px' }}><Pill tone="neutral" dot size="sm">{t(`pages.orders.zoho.${order.zohoSync.status}`)}</Pill></td>}
                  <td style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--fg)' }}>{formatMoney(order.totalAmount, order.currency, i18n.language, fallbackCurrency)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'end' }} onClick={event => event.stopPropagation()}>
                    <button
                      type="button"
                      aria-label={String(t('pages.orders.actions.openDetail', { order: orderLabel(order) }))}
                      onClick={() => openDetail(order.id)}
                      style={{ background: 'transparent', border: '1px solid var(--border-subtle)', width: 28, height: 28, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--fg-muted)' }}
                    >
                      <Icon size={16}>{I.eye}</Icon>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
          <span style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
            {t('pages.orders.pagination', {
              from: total === 0 ? 0 : page * PAGE_SIZE + 1,
              to: Math.min((page + 1) * PAGE_SIZE, total),
              total,
            })}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button kind="secondary" size="xs" disabled={page === 0 || loading} onClick={() => setPage(current => Math.max(0, current - 1))}>{t('pages.orders.paginationPrev')}</Button>
            <Button kind="secondary" size="xs" disabled={!data || page >= data.totalPages - 1 || loading} onClick={() => setPage(current => current + 1)}>{t('pages.orders.paginationNext')}</Button>
          </div>
        </div>
      </Card>

      {selectedOrderId !== null && (
        <Card padding={22} style={{ marginTop: 18 }}>
          <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : undefined, justifyContent: isMobile ? undefined : 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: isMobile ? 12 : 16, marginBottom: 18 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 6 }}>{t('pages.orders.detail.title')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
                <h2 style={{ ...TEXT_WRAP_STYLE, margin: 0, fontSize: 22, lineHeight: 1.1, color: 'var(--brand-900)' }}>{selectedOrderLabel}</h2>
                {detail && <Pill tone={statusTone(detail.status)} dot size="sm">{t(`pages.orders.status.${detail.status}`)}</Pill>}
              </div>
            </div>
            <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : undefined, gap: 8, flexWrap: isMobile ? undefined : 'wrap', justifyContent: isMobile ? undefined : 'flex-end' }}>
              <Button kind="secondary" size="xs" icon={I.refresh} onClick={refreshSelectedOrderFromChannel} disabled={!detail || channelRefreshLoading || detailLoading || zohoSendLoading || zohoResetLoading} style={mobileButtonStyle(isMobile)}>
                {channelRefreshLoading ? t('pages.orders.actions.refreshingFromChannel') : t('pages.orders.actions.refreshFromChannel')}
              </Button>
              <Button kind="secondary" size="xs" icon={I.x} onClick={closeDetail} style={mobileButtonStyle(isMobile)}>{t('pages.orders.actions.closeDetail')}</Button>
            </div>
          </div>

          {detailLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
              <Icon size={16}>{I.refresh}</Icon>
              {t('pages.orders.detail.loading')}
            </div>
          )}

          {detailError !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
              <Icon size={16}>{I.alert}</Icon>
              {String(t(errorMessageKey(detailError), { defaultValue: String(t('pages.orders.errors.codes.REQUEST_FAILED')) }))}
            </div>
          )}

          {channelRefreshError !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
              <Icon size={16}>{I.alert}</Icon>
              {String(t(errorMessageKey(channelRefreshError), { defaultValue: String(t('pages.orders.errors.codes.REQUEST_FAILED')) }))}
            </div>
          )}

          {detail && (
            <div style={{ display: 'grid', gap: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(160px, 1fr))'), gap: 10 }}>
                <DetailField label={t('pages.orders.detail.customer')} value={detail.customerName ?? EMPTY_VALUE} />
                <DetailField label={t('pages.orders.detail.email')} value={detail.customerEmail ?? EMPTY_VALUE} />
                <DetailField label={t('pages.orders.detail.phone')} value={detail.customerPhone ?? EMPTY_VALUE} />
                <DetailField label={t('pages.orders.detail.placedAt')} value={formatDate(displayDate(detail), i18n.language)} />
              </div>

              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: 15, color: 'var(--brand-900)' }}>{t('pages.orders.detail.payment')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(140px, 1fr))'), gap: 10 }}>
                  <DetailField label={t('pages.orders.detail.paymentStatus')} value={detail.paymentStatus ?? EMPTY_VALUE} />
                  <DetailField label={t('pages.orders.detail.paymentMethod')} value={paymentMethodDisplay(detail)} />
                  <DetailField label={t('pages.orders.detail.paymentType')} value={detail.paymentMethodType ?? EMPTY_VALUE} />
                  <DetailField label={t('pages.orders.detail.paymentTransactionStatus')} value={paymentSecondary(detail) ?? EMPTY_VALUE} />
                </div>
              </div>

              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: 15, color: 'var(--brand-900)' }}>{t('pages.orders.detail.delivery')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(160px, 1fr))'), gap: 10 }}>
                  <DetailField label={t('pages.orders.detail.shippingCompany')} value={detail.shippingCompany ?? EMPTY_VALUE} />
                  <DetailField label={t('pages.orders.detail.shippingMethod')} value={detail.shippingMethod ?? EMPTY_VALUE} />
                  <DetailField label={t('pages.orders.detail.fulfillmentChannel')} value={detail.fulfillmentChannel ?? EMPTY_VALUE} />
                  <DetailField label={t('pages.orders.detail.fulfillmentStatus')} value={detail.fulfillmentStatus ?? EMPTY_VALUE} />
                  <DetailTextBlock label={t('pages.orders.detail.deliveryAddress')} value={detail.deliveryAddress ?? EMPTY_VALUE} />
                </div>
              </div>

              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: 15, color: 'var(--brand-900)' }}>{t('pages.orders.detail.totals')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(140px, 1fr))'), gap: 10 }}>
                  <DetailField label={t('pages.orders.detail.subtotal')} value={formatMoney(detail.subtotalAmount, detail.currency, i18n.language, fallbackCurrency)} />
                  <DetailField label={t('pages.orders.detail.tax')} value={formatMoney(detail.taxAmount, detail.currency, i18n.language, fallbackCurrency)} />
                  <DetailField label={t('pages.orders.detail.shipping')} value={formatMoney(detail.shippingAmount, detail.currency, i18n.language, fallbackCurrency)} />
                  <DetailField label={t('pages.orders.detail.discount')} value={formatMoney(detail.discountAmount, detail.currency, i18n.language, fallbackCurrency)} />
                  <DetailField label={t('pages.orders.detail.total')} value={formatMoney(detail.totalAmount, detail.currency, i18n.language, fallbackCurrency)} />
                </div>
              </div>

              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: 15, color: 'var(--brand-900)' }}>{t('pages.orders.detail.invoiceBreakdown')}</h3>
                {isMobile ? (
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden', display: 'grid' }}>
                    {(detail.invoiceBreakdown ?? []).length === 0 ? (
                      <div style={{ padding: 18, textAlign: 'center', color: 'var(--fg-muted)' }}>{t('pages.orders.detail.noInvoiceBreakdown')}</div>
                    ) : (
                      (detail.invoiceBreakdown ?? []).map((row, index) => (
                        <div key={`${row.sortOrder ?? 'row'}-${row.code ?? row.label ?? ''}`} style={{ padding: 14, borderTop: index === 0 ? undefined : '1px solid var(--border-subtle)', display: 'grid', gridTemplateColumns: MOBILE_SINGLE_COLUMN, gap: 10, background: 'white' }}>
                          <MobileDetailValue label={t('pages.orders.detail.invoiceDescription')} value={row.label?.trim() || row.code?.trim() || EMPTY_VALUE} strong />
                          <MobileDetailValue label={t('pages.orders.detail.invoiceCode')} value={row.code?.trim() || EMPTY_VALUE} mono />
                          <MobileDetailValue label={t('pages.orders.detail.invoiceAmount')} value={invoiceAmountDisplay(row, detail.currency, i18n.language, fallbackCurrency)} strong />
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--ink-50)' }}>
                          {[t('pages.orders.detail.invoiceDescription'), t('pages.orders.detail.invoiceCode'), t('pages.orders.detail.invoiceAmount')].map((header, index) => (
                            <th key={header} style={{ padding: '10px 12px', textAlign: index === 2 ? 'end' : 'start', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.invoiceBreakdown ?? []).length === 0 && (
                          <tr>
                            <td colSpan={3} style={{ padding: 18, textAlign: 'center', color: 'var(--fg-muted)' }}>{t('pages.orders.detail.noInvoiceBreakdown')}</td>
                          </tr>
                        )}
                        {(detail.invoiceBreakdown ?? []).map(row => (
                          <tr key={`${row.sortOrder ?? 'row'}-${row.code ?? row.label ?? ''}`} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--fg)' }}>{row.label?.trim() || row.code?.trim() || EMPTY_VALUE}</td>
                            <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-muted)' }}>{row.code?.trim() || EMPTY_VALUE}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'end', fontWeight: 700, color: 'var(--fg)' }}>{invoiceAmountDisplay(row, detail.currency, i18n.language, fallbackCurrency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: 15, color: 'var(--brand-900)' }}>{t('pages.orders.detail.lineItems')}</h3>
                {isMobile ? (
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden', display: 'grid' }}>
                    {detail.lineItems.length === 0 ? (
                      <div style={{ padding: 18, textAlign: 'center', color: 'var(--fg-muted)' }}>{t('pages.orders.detail.noLineItems')}</div>
                    ) : (
                      detail.lineItems.map((item, index) => (
                        <div key={item.id} style={{ padding: 14, borderTop: index === 0 ? undefined : '1px solid var(--border-subtle)', display: 'grid', gridTemplateColumns: MOBILE_SINGLE_COLUMN, gap: 10, background: 'white' }}>
                          <MobileDetailValue label={t('pages.orders.detail.item')} value={item.name ?? EMPTY_VALUE} strong />
                          <MobileDetailValue label={t('pages.orders.detail.sku')} value={item.sku ?? EMPTY_VALUE} mono />
                          <MobileDetailValue label={t('pages.orders.detail.quantity')} value={item.quantity?.toLocaleString(i18n.language) ?? EMPTY_VALUE} />
                          <MobileDetailValue label={t('pages.orders.detail.unitPrice')} value={formatMoney(item.unitPrice, detail.currency, i18n.language, fallbackCurrency)} />
                          <MobileDetailValue label={t('pages.orders.detail.lineTotal')} value={formatMoney(item.totalPrice, detail.currency, i18n.language, fallbackCurrency)} strong />
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--ink-50)' }}>
                          {[t('pages.orders.detail.item'), t('pages.orders.detail.sku'), t('pages.orders.detail.quantity'), t('pages.orders.detail.unitPrice'), t('pages.orders.detail.lineTotal')].map((header, index) => (
                            <th key={header} style={{ padding: '10px 12px', textAlign: index >= 2 ? 'end' : 'start', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.lineItems.length === 0 && (
                          <tr>
                            <td colSpan={5} style={{ padding: 18, textAlign: 'center', color: 'var(--fg-muted)' }}>{t('pages.orders.detail.noLineItems')}</td>
                          </tr>
                        )}
                        {detail.lineItems.map(item => (
                          <tr key={item.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--fg)' }}>{item.name ?? EMPTY_VALUE}</td>
                            <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-muted)' }}>{item.sku ?? EMPTY_VALUE}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'end', color: 'var(--fg-muted)' }}>{item.quantity?.toLocaleString(i18n.language) ?? EMPTY_VALUE}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'end', color: 'var(--fg-muted)' }}>{formatMoney(item.unitPrice, detail.currency, i18n.language, fallbackCurrency)}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'end', fontWeight: 700, color: 'var(--fg)' }}>{formatMoney(item.totalPrice, detail.currency, i18n.language, fallbackCurrency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {showZohoDetail && (
                <Card padding={16} style={{ background: 'var(--ink-50)' }}>
                  <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : undefined, alignItems: isMobile ? 'stretch' : 'center', justifyContent: isMobile ? undefined : 'space-between', gap: 12, flexWrap: isMobile ? undefined : 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...TEXT_WRAP_STYLE, fontSize: 14, fontWeight: 800, color: 'var(--brand-900)', marginBottom: 4 }}>{t('pages.orders.detail.zohoAction')}</div>
                      <div style={{ ...TEXT_WRAP_STYLE, fontSize: 12.5, color: 'var(--fg-muted)' }}>{t(zohoDetailConnected ? 'pages.orders.zoho.handoffHelp' : 'pages.orders.zoho.reconnectHelp')}</div>
                    </div>
                    {zohoDetailConnected && (
                    <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : undefined, gap: 8, flexWrap: isMobile ? undefined : 'wrap', justifyContent: isMobile ? undefined : 'flex-end' }}>
                      <Button kind="secondary" size="sm" onClick={loadZohoPreview} disabled={zohoPreviewLoading || zohoSendLoading || zohoResetLoading} style={mobileButtonStyle(isMobile)}>
                        {zohoPreviewLoading ? t('pages.orders.actions.previewingZoho') : t('pages.orders.actions.previewZoho')}
                      </Button>
                      {zohoPreview?.inventoryConnectionId != null && <Button kind="secondary" size="sm" onClick={() => onOpenOrderRouting?.(zohoPreview.inventoryConnectionId!, detail.id)} style={mobileButtonStyle(isMobile)}>{t('pages.orders.actions.openOrderRouting')}</Button>}
                      {canResetZohoInvoice && (
                        <Button kind="secondary" size="sm" onClick={resetSelectedOrderZohoInvoice} disabled={zohoSendLoading || zohoResetLoading} style={mobileButtonStyle(isMobile)}>
                          {zohoResetLoading ? t('pages.orders.actions.resettingZohoInvoice') : t('pages.orders.actions.resetZohoInvoice')}
                        </Button>
                      )}
                      {canResetZohoSync && (
                        <Button kind="danger" size="sm" onClick={resetSelectedOrderZohoSync} disabled={zohoSendLoading || zohoResetLoading} style={mobileButtonStyle(isMobile)}>
                          {zohoResetLoading ? t('pages.orders.actions.resettingZohoSync') : t('pages.orders.actions.resetZohoSync')}
                        </Button>
                      )}
                      <Button kind="secondary" size="sm" onClick={sendSelectedOrderToZoho} disabled={!detail.zohoSync.canSend || zohoSendLoading || zohoResetLoading} style={mobileButtonStyle(isMobile)}>
                        {zohoSendLoading ? t('pages.orders.actions.sendingZoho') : t(`pages.orders.actions.sendZoho.${detail.zohoSync.nextAction}`, { defaultValue: zohoNextActionLabel(detail.zohoSync.nextAction, t) })}
                      </Button>
                    </div>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(150px, 1fr))'), gap: 10, marginTop: 12 }}>
                    <DetailField label={t('pages.orders.detail.zohoStatus')} value={String(t(`pages.orders.zoho.${detail.zohoSync.status}`, { defaultValue: detail.zohoSync.status }))} />
                    <DetailField label={t('pages.orders.detail.zohoNextAction')} value={zohoNextActionLabel(detail.zohoSync.nextAction, t)} />
                    <DetailField label={t('pages.orders.detail.zohoReason')} value={zohoReasonLabel(detail.zohoSync.reasonCode, t)} />
                    <DetailField label={t('pages.orders.detail.zohoPolicyAction')} value={detail.zohoSync.policyAction ? zohoNextActionLabel(detail.zohoSync.policyAction, t) : EMPTY_VALUE} />
                    <DetailField label={t('pages.orders.detail.zohoSalesOrderId')} value={detail.zohoSync.salesOrderId ?? EMPTY_VALUE} />
                    <DetailField label={t('pages.orders.detail.zohoInvoiceId')} value={detail.zohoSync.invoiceId ?? EMPTY_VALUE} />
                  </div>

                  {detail.zohoSync.lastError && (
                    <div style={{ marginTop: 10 }}>
                      <DetailTextBlock label={t('pages.orders.detail.zohoLastError')} value={detail.zohoSync.lastError} />
                    </div>
                  )}

                  {zohoSendError !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700, marginTop: 12 }}>
                      <Icon size={16}>{I.alert}</Icon>
                      {String(t(errorMessageKey(zohoSendError), { defaultValue: String(t('pages.orders.errors.codes.REQUEST_FAILED')) }))}
                    </div>
                  )}

                  {zohoPreviewError !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700, marginTop: 12 }}>
                      <Icon size={16}>{I.alert}</Icon>
                      {String(t(errorMessageKey(zohoPreviewError), { defaultValue: String(t('pages.orders.errors.codes.REQUEST_FAILED')) }))}
                    </div>
                  )}

                  {zohoDetailConnected && zohoPreview && <ZohoPreviewPanel preview={zohoPreview} t={t} isMobile={isMobile} />}
                </Card>
              )}

              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: 15, color: 'var(--brand-900)' }}>{t('pages.orders.history.title')}</h3>
                {detail.statusHistory.length === 0 ? (
                  <Card padding={16} style={{ background: 'var(--ink-50)' }}>
                    <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.orders.history.empty')}</div>
                  </Card>
                ) : (
                  <Card padding={16} style={{ background: 'var(--ink-50)' }}>
                    <div style={{ display: 'grid', gap: 14 }}>
                      {detail.statusHistory.map(event => <StatusHistoryRow key={event.id} event={event} locale={i18n.language} t={t} />)}
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
